import React from 'react';
import { useTranslation } from 'react-i18next';
import { CheckToggle } from './CheckToggle';

interface NotifyRoleTogglesProps<TRole extends string> {
  /** Selectable roles in canonical order; the selection preserves this order. */
  options: TRole[];
  selected: TRole[];
  onChange: (roles: TRole[]) => void;
  /** Translation key prefix holding a `${role}.label` per option. */
  translationPrefix: string;
  /** Prefix for the `data-testid`s of each role row and toggle. */
  testIdPrefix: string;
}

/**
 * Recipient-role toggles for a notification card: a column of check circles,
 * each next to its role name. Toggling a role on keeps the canonical option
 * order. Presentational — the selection is owned by the containing card.
 */
export const NotifyRoleToggles = <TRole extends string>({
  options,
  selected,
  onChange,
  translationPrefix,
  testIdPrefix,
}: NotifyRoleTogglesProps<TRole>): React.ReactElement => {
  const { t } = useTranslation('admin');

  const toggleNotifyRole = (role: TRole, checked: boolean) => {
    const next = checked
      ? options.filter((option) => option === role || selected.includes(option))
      : selected.filter((option) => option !== role);
    onChange(next);
  };

  return (
    <div className="flex flex-col gap-2">
      {options.map((role) => {
        const checked = selected.includes(role);
        return (
          <div
            key={role}
            className="flex items-center gap-2"
            data-testid={`${testIdPrefix}-notify-role-${role}-label`}
          >
            <CheckToggle
              checked={checked}
              onCheckedChange={(next) => toggleNotifyRole(role, next)}
              testId={`${testIdPrefix}-notify-role-${role}`}
            />
            <span className="text-sm font-medium">{t(`${translationPrefix}.${role}.label`)}</span>
          </div>
        );
      })}
    </div>
  );
};
